import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../components/ui/Header';
import AuthenticationGuard from '../components/ui/AuthenticationGuard';
import SmartLoadingBuffer from '../components/ui/SmartLoadingBuffer';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import useAuth from '../hooks/useAuth';
import { createHabit, deleteHabitLog, fetchHabitsWithLogs, upsertHabitLog } from '../services/habitsService';

const HabitsPage = () => {
  const { user, initializing } = useAuth();
  const [habits, setHabits] = useState([]);
  const [habitLogs, setHabitLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [syncingHabitId, setSyncingHabitId] = useState(null);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const initialFormState = useMemo(() => ({
    name: '',
    focusArea: '',
    motivation: '',
    targetType: 'daily',
    targetAmount: '',
    timeOfDay: '',
    obstacle: '',
    support: '',
    startDate: ''
  }), []);
  const [formData, setFormData] = useState(initialFormState);

  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const loadHabits = useCallback(async () => {
    if (!user?.id) {
      setHabits([]);
      setHabitLogs([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { habits: fetchedHabits, logs } = await fetchHabitsWithLogs(user.id);
      setHabits(fetchedHabits);
      setHabitLogs(logs);
    } catch (err) {
      console.error('Failed to load habits:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!initializing) {
      loadHabits();
    }
  }, [initializing, loadHabits]);

  useEffect(() => {
    if (!initializing && !isLoading && habits?.length === 0) {
      setShowForm(true);
    }
  }, [initializing, isLoading, habits?.length]);

  const habitLogsMap = useMemo(() => {
    return habitLogs.reduce((acc, log) => {
      if (!acc[log.habit_id]) {
        acc[log.habit_id] = [];
      }
      acc[log.habit_id].push(log);
      return acc;
    }, {});
  }, [habitLogs]);

  const isHabitCompletedToday = useCallback(
    (habitId) => {
      return habitLogsMap?.[habitId]?.some(
        (log) => log.logged_on === todayISO && log.status === 'completed'
      );
    },
    [habitLogsMap, todayISO]
  );

  const streakForHabit = useCallback(
    (habitId) => {
      const logs = habitLogsMap?.[habitId]?.filter((log) => log.status === 'completed');
      if (!logs?.length) {
        return 0;
      }

      const completedDates = new Set(logs.map((log) => log.logged_on));
      let streak = 0;
      const cursor = new Date();
      cursor.setHours(0, 0, 0, 0);

      while (true) {
        const iso = cursor.toISOString().slice(0, 10);
        if (completedDates.has(iso)) {
          streak += 1;
          cursor.setDate(cursor.getDate() - 1);
        } else {
          break;
        }
      }

      return streak;
    },
    [habitLogsMap]
  );

  const stats = useMemo(() => {
    if (!habits?.length) {
      return {
        completedToday: 0,
        total: 0,
        bestStreak: 0,
      };
    }

    const completedToday = habits.filter((habit) => isHabitCompletedToday(habit.id)).length;
    const bestStreak = habits.reduce((max, habit) => {
      const streak = streakForHabit(habit.id);
      return streak > max ? streak : max;
    }, 0);

    return {
      completedToday,
      total: habits.length,
      bestStreak,
    };
  }, [habits, isHabitCompletedToday, streakForHabit]);

  const handleToggleHabitCompletion = useCallback(
    async (habitId) => {
      if (!user?.id) {
        return;
      }

      setSyncingHabitId(habitId);
      setError(null);

      const completed = isHabitCompletedToday(habitId);

      try {
        if (completed) {
          await deleteHabitLog({ habitId, userId: user.id, loggedOn: todayISO });
          setHabitLogs((prev) =>
            prev.filter((log) => !(log.habit_id === habitId && log.logged_on === todayISO))
          );
        } else {
          const log = await upsertHabitLog({
            habitId,
            userId: user.id,
            status: 'completed',
            loggedOn: todayISO,
          });

          setHabitLogs((prev) => {
            const otherLogs = prev.filter(
              (existing) => !(existing.habit_id === habitId && existing.logged_on === todayISO)
            );
            return [...otherLogs, log];
          });
        }
      } catch (err) {
        console.error('Failed to toggle habit completion:', err);
        setError(err);
      } finally {
        setSyncingHabitId(null);
      }
    },
    [user?.id, isHabitCompletedToday, todayISO]
  );

  const handleFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
    setFormSuccess(null);
  }, []);

  const handleCreateHabit = useCallback(async (event) => {
    event.preventDefault();
    if (!user?.id) {
      return;
    }

    if (!formData.name?.trim()) {
      setFormError('Give your habit a short, memorable name.');
      return;
    }

    const parsedAmount = Number(formData.targetAmount);
    if (Number.isNaN(parsedAmount) || parsedAmount < 0) {
      setFormError('Enter a valid target amount (e.g., minutes per day or sessions per week).');
      return;
    }

    const targetPerWeek = formData.targetType === 'daily' ? Math.round(parsedAmount * 7) : Math.round(parsedAmount);
    const descriptionLines = [
      formData.motivation ? `Motivation: ${formData.motivation}` : null,
      formData.obstacle ? `Potential obstacle: ${formData.obstacle}` : null,
      formData.support ? `Accountability: ${formData.support}` : null,
      formData.timeOfDay ? `Preferred time: ${formData.timeOfDay}` : null,
      formData.startDate ? `Starting on: ${formData.startDate}` : null
    ].filter(Boolean);

    const description = descriptionLines.join('\n');

    setIsSubmitting(true);
    setFormError(null);

    try {
      await createHabit({
        user_id: user.id,
        title: formData.name.trim(),
        category: formData.focusArea?.trim() || 'General',
        target_per_week: targetPerWeek > 0 ? targetPerWeek : null,
        description: description || null,
        icon: 'Target'
      });

      setFormSuccess('Habit saved! Your answers will help tailor future habit recommendations.');
      setFormData(initialFormState);
      setShowForm(false);
      await loadHabits();
    } catch (err) {
      console.error('Failed to create habit:', err);
      setFormError(err?.message || 'Unable to save habit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, initialFormState, loadHabits, user?.id]);

  if (initializing || isLoading) {
    return (
      <AuthenticationGuard>
        <div className="min-h-screen bg-background">
          <Header />
          <div className="pt-16">
            <SmartLoadingBuffer
              className="min-h-[60vh]"
              title="Loading your habits"
              message="We’re syncing routines, logs, and insights to help you stay consistent."
              hint="Cogito is preparing your habit dashboard."
            />
          </div>
        </div>
      </AuthenticationGuard>
    );
  }

  return (
    <AuthenticationGuard>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">My Habits</h1>
                  <p className="text-muted-foreground">Track your daily habits and build consistency</p>
                </div>
                <Button 
                  className="hidden sm:flex"
                  onClick={() => {
                    setShowForm((prev) => !prev);
                    setFormError(null);
                    setFormSuccess(null);
                  }}
                >
                  <Icon name={showForm ? 'X' : 'Plus'} size={16} className="mr-2" />
                  {showForm ? 'Close planner' : 'Plan a habit'}
                </Button>
              </div>
            </div>

            {formSuccess && (
              <div className="mb-6 rounded-lg border border-success/20 bg-success/10 p-4 text-sm text-success">
                {formSuccess}
              </div>
            )}

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="CheckCircle" size={24} className="text-success" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {stats.completedToday}
                    </p>
                    <p className="text-sm text-muted-foreground">Completed Today</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                    <Icon name="Target" size={24} className="text-warning" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                    <p className="text-sm text-muted-foreground">Active Habits</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="TrendingUp" size={24} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {stats.bestStreak}
                    </p>
                    <p className="text-sm text-muted-foreground">Best Streak</p>
                  </div>
                </div>
              </div>
            </div>

            {showForm && (
              <div className="mb-8 bg-card border border-border rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="PenSquare" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Design a habit plan</h2>
                    <p className="text-sm text-muted-foreground">Answer a few prompts so we can personalise future habit suggestions.</p>
                  </div>
                </div>

                {formError && (
                  <div className="mb-4 rounded-lg border border-error/20 bg-error/10 p-3 text-sm text-error">
                    {formError}
                  </div>
                )}

                <form className="space-y-6" onSubmit={handleCreateHabit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">What do you want to call this habit?</span>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                        placeholder="e.g., 15-minute morning reading"
                        required
                      />
                    </label>
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">Which area does this support?</span>
                      <select
                        name="focusArea"
                        value={formData.focusArea}
                        onChange={handleFormChange}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      >
                        <option value="">Select focus</option>
                        <option value="Skill building">Skill building</option>
                        <option value="Wellness">Wellness</option>
                        <option value="Career">Career</option>
                        <option value="Learning">Learning</option>
                        <option value="Creative">Creative</option>
                        <option value="Other">Other</option>
                      </select>
                    </label>
                  </div>

                  <label className="flex flex-col space-y-2">
                    <span className="text-sm font-medium text-foreground">Why is this habit important right now?</span>
                    <textarea
                      name="motivation"
                      value={formData.motivation}
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 min-h-[90px]"
                      placeholder="Write a sentence or two about the impact you want."
                    />
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">How often will you practice?</span>
                      <select
                        name="targetType"
                        value={formData.targetType}
                        onChange={handleFormChange}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      >
                        <option value="daily">Minutes per day</option>
                        <option value="weekly">Sessions per week</option>
                      </select>
                    </label>
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">Target amount</span>
                      <input
                        type="number"
                        min="0"
                        name="targetAmount"
                        value={formData.targetAmount}
                        onChange={handleFormChange}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                        placeholder="e.g., 15"
                      />
                    </label>
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">Preferred time of day</span>
                      <select
                        name="timeOfDay"
                        value={formData.timeOfDay}
                        onChange={handleFormChange}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      >
                        <option value="">Choose time</option>
                        <option value="Morning">Morning</option>
                        <option value="Afternoon">Afternoon</option>
                        <option value="Evening">Evening</option>
                        <option value="Flexible">Flexible</option>
                      </select>
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">What might get in the way?</span>
                      <textarea
                        name="obstacle"
                        value={formData.obstacle}
                        onChange={handleFormChange}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 min-h-[70px]"
                        placeholder="Identify a barrier you want to plan around."
                      />
                    </label>
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">Who or what keeps you accountable?</span>
                      <textarea
                        name="support"
                        value={formData.support}
                        onChange={handleFormChange}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 min-h-[70px]"
                        placeholder="e.g., study buddy, calendar reminder, mentor."
                      />
                    </label>
                  </div>

                  <label className="flex flex-col space-y-2 max-w-xs">
                    <span className="text-sm font-medium text-foreground">When would you like to start?</span>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleFormChange}
                      className="rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </label>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
                    <Button type="submit" disabled={isSubmitting} className="sm:w-auto">
                      {isSubmitting ? 'Saving...' : 'Save habit plan'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="sm:w-auto"
                      onClick={() => {
                        setShowForm(false);
                        setFormData(initialFormState);
                        setFormError(null);
                        setFormSuccess(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {error && (
              <div className="mb-6 rounded-lg border border-error/20 bg-error/10 p-4 text-sm text-error">
                {error?.message || 'Something went wrong while syncing your habits. Please try again.'}
              </div>
            )}

            {/* Habits List */}
            <div className="space-y-4">
              {habits?.map((habit) => (
                <div
                  key={habit?.id}
                  className="bg-card rounded-xl border border-border p-6 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => handleToggleHabitCompletion(habit?.id)}
                        className={`w-12 h-12 rounded-lg flex items-center justify-center transition-colors ${
                          isHabitCompletedToday(habit?.id)
                            ? 'bg-success text-white'
                            : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                        }`}
                        disabled={syncingHabitId === habit?.id}
                        aria-pressed={isHabitCompletedToday(habit?.id)}
                      >
                        <Icon 
                          name={isHabitCompletedToday(habit?.id) ? 'Check' : habit?.icon || 'CheckCircle'}
                          size={20} 
                        />
                      </button>
                      
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">
                          {habit?.title || 'Untitled habit'}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {habit?.description}
                        </p>
                        <div className="flex items-center space-x-4 mt-2">
                          <span className="text-xs bg-muted px-2 py-1 rounded-full capitalize">
                            {habit?.category || 'General'}
                          </span>
                          <span className="text-xs bg-muted px-2 py-1 rounded-full">
                            Target: {habit?.target_per_week ? `${habit.target_per_week} / week` : 'Daily'}
                          </span>
                          <span className="text-xs bg-muted px-2 py-1 rounded-full">
                            Streak: {streakForHabit(habit?.id)} days
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-sm font-medium text-foreground">
                        {isHabitCompletedToday(habit?.id) ? 'Completed Today' : 'Pending'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {!habits?.length && !error && (
                <div className="text-center py-12 bg-card rounded-xl border border-border">
                  <div className="w-16 h-16 bg-muted/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon name="ClipboardList" size={24} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No habits yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Start by adding a habit to track your progress and build momentum.
                  </p>
                </div>
              )}
            </div>

            {/* Mobile Add Button */}
            <div className="sm:hidden fixed bottom-6 right-6">
              <Button
                size="lg"
                className="w-14 h-14 rounded-full shadow-lg"
                onClick={() => {
                  setShowForm(true);
                  setFormError(null);
                  setFormSuccess(null);
                }}
              >
                <Icon name="Plus" size={24} />
              </Button>
            </div>
          </div>
        </main>
      </div>
    </AuthenticationGuard>
  );
};

export default HabitsPage;